import type { FC } from "react";
import type { FileEntry } from "@bloomstack/apis/files";
import dayjs from "dayjs";
import { GlobeAltIcon, UserGroupIcon } from "@heroicons/react/20/solid";
import { DownloadIcon } from "lucide-react";

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1073741824).toFixed(1)} GB`;
}

type FileMetadataPanelProps = {
  file: FileEntry;
};

export const FileMetadataPanel: FC<FileMetadataPanelProps> = ({ file }) => {
  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = `/api/file/${file.id}`;
    link.download = file.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="flex shrink-0 items-center gap-4 border-t border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 px-3 py-2 text-xs text-zinc-500 dark:text-zinc-400">
      <span className="truncate max-w-[200px] font-medium text-zinc-700 dark:text-zinc-300">
        {file.fileName}
      </span>
      <span>{file.fileMime}</span>
      <span>{formatFileSize(file.fileSize)}</span>
      <span>{dayjs(file.createdAt).format("MMM D, YYYY")}</span>

      {file.isPublic ? (
        <span className="flex items-center gap-1 rounded-full bg-green-100 dark:bg-green-900/30 px-2 py-0.5 text-green-700 dark:text-green-400">
          <GlobeAltIcon className="w-3 h-3" />
          Public
        </span>
      ) : file.isShared ? (
        <span className="flex items-center gap-1 rounded-full bg-blue-100 dark:bg-blue-900/30 px-2 py-0.5 text-blue-700 dark:text-blue-400">
          <UserGroupIcon className="w-3 h-3" />
          Shared
        </span>
      ) : null}

      <button
        className="ml-auto flex items-center gap-1 rounded px-2 py-1 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
        onClick={handleDownload}
        title="Download"
      >
        <DownloadIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};
